/**
 * Groups service — core CRUD + listing/search.
 * Ported 1:1 from the legacy flat services/groups.js (URL strings preserved
 * byte-for-byte, including param quirks). Response shapes verified against
 * docs/groups-migration/Api-response/.
 */
import constants from '../../constants'
import { getChatApiBase, getChatApiHeaders } from '../chatApiClient'
import axios from '../axiosInstance'
import {
  ChatApp,
  AddGroupPayload,
  UpdateGroupPayload,
  GroupsListFilters,
  GroupsListResponse,
  GroupResponse,
  SuccessMessageResponse,
} from './types'

/**
 * LEGACY ERROR CONTRACT (deliberate, ported from services/groups.js): resolves
 * with `false` on a network/non-response error rather than rejecting — the
 * frozen apps/GROUPS_LIST handler (redux/apps/sagas.js:4187) has no try/catch
 * and depends on a falsy return to fall through safely. Normalize only when
 * that consumer retires (Plan Phase 7).
 */
export async function list(
  app: ChatApp,
  apiHost: string,
  pageNo = 1,
  searchText = '',
  filters: GroupsListFilters = {},
): Promise<GroupsListResponse | false> {
  let URL = `${getChatApiBase(app, apiHost)}/groups?per_page=${
    constants.perPageInListings
  }&page=${pageNo}&useCursoredIndex=1`

  const searchKey = filters.searchKey || searchText
  if (searchKey) {
    URL += `&searchKey=${encodeURIComponent(searchKey)}`
  }

  if (filters.guid) {
    URL += `&guid=${encodeURIComponent(filters.guid)}`
  } else if (filters.guids && filters.guids.length > 0) {
    URL += `&guids=${filters.guids.map(g => encodeURIComponent(g)).join(',')}`
  }

  if (filters.type) {
    URL += `&type=${encodeURIComponent(filters.type)}`
  }

  if (filters.membersCount && filters.membersCount.operator && filters.membersCount.value !== undefined) {
    URL += `&membersCount[${encodeURIComponent(filters.membersCount.operator)}]=${encodeURIComponent(
      String(filters.membersCount.value),
    )}`
  }

  if (filters.createdAtFrom) {
    URL += `&createdAt[from]=${encodeURIComponent(filters.createdAtFrom)}`
  }
  if (filters.createdAtTo) {
    URL += `&createdAt[to]=${encodeURIComponent(filters.createdAtTo)}`
  }

  return axios
    .get(`${URL}`, {
      headers: { ...getChatApiHeaders(app), 'Cache-Control': 'no-cache' },
      params: { _: Date.now() },
    })
    .then(res => res.data)
    .catch(error => {
      if (error && error.response && error.response.data) return error.response.data
      return false
    })
}

export async function add(
  post: AddGroupPayload,
  app: ChatApp,
  apiHost: string,
): Promise<GroupResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups`
  return axios
    .post(`${URL}`, post, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function get(id: string, app: ChatApp, apiHost: string): Promise<GroupResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${id}`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function update(
  id: string,
  post: UpdateGroupPayload,
  app: ChatApp,
  apiHost: string,
): Promise<GroupResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${id}`
  return axios
    .put(`${URL}`, post, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function del(
  id: string,
  app: ChatApp,
  apiHost: string,
): Promise<SuccessMessageResponse> {
  const URL = `${getChatApiBase(app, apiHost)}/groups/${id}`
  return axios
    .delete(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

export async function search(
  app: ChatApp,
  apiHost: string,
  searchText: string,
  pageNo = 1,
): Promise<GroupsListResponse> {
  // Encoded like `list()` does — a raw `&`/`#`/`+` in a group name would
  // otherwise truncate the query and return the wrong results.
  const URL = `${getChatApiBase(app, apiHost)}/groups?searchKey=${encodeURIComponent(
    searchText,
  )}&per_page=${constants.perPageInListings}&page=${pageNo}&useCursoredIndex=1`
  return axios
    .get(`${URL}`, {
      headers: getChatApiHeaders(app),
    })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}
